import { useCallback } from 'react';
import { oktaProvider } from '../../../authProvider';
import { useSubscriptionParams } from './useSubscriptionParams';

/**
 * Custom hook to handle Okta sign-in flows for subscription forms
 * Encapsulates the different sign-in scenarios with proper state management
 *
 * @returns Object with sign-in methods for different scenarios
 */
export const useOktaSignIn = () => {
    const { allParams } = useSubscriptionParams();

    /**
     * Sign in with billing code
     * Used when user has a billing association code
     *
     * @param email - User's email address (used as login hint)
     * @param billingCode - Billing association code to include in state
     */
    const signInWithBillingCode = useCallback((email: string, billingCode: string) => {
        const options = {
            state: {
                ...allParams,
                BillingAssociationCode: billingCode
            },
            loginHint: email
        };
        oktaProvider.signIn(options);
    }, [allParams]);

    /**
     * Sign in without billing code
     * Used for trial users or when no billing code is provided
     *
     * @param email - User's email address (used as login hint)
     */
    const signInWithoutCode = useCallback((email: string) => {
        const options = {
            state: allParams,
            loginHint: email
        };
        oktaProvider.signIn(options);
    }, [allParams]);

    /**
     * Sign in with custom state
     * Used when you need to pass additional state parameters
     *
     * @param email - User's email address (used as login hint)
     * @param customState - Additional state to merge with URL params
     */
    const signInWithState = useCallback((email?: string, customState?: Record<string, any>) => {
        const options = {
            state: {
                ...allParams,
                ...customState
            },
            ...(email && { loginHint: email })
        };
        oktaProvider.signIn(options);
    }, [allParams]);

    /**
     * Sign in with just state (no login hint)
     * Used when email is not available or not needed
     *
     * @param customState - State to pass to Okta (defaults to URL params)
     */
    const signInWithStateOnly = useCallback((customState?: Record<string, any>) => {
        const options = {
            state: customState || allParams
        };
        oktaProvider.signIn(options);
    }, [allParams]);

    return {
        signInWithBillingCode,
        signInWithoutCode,
        signInWithState,
        signInWithStateOnly
    };
};
